import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import { formatMoney } from '../Shared/Formatters';

/**
 * TenantYoYChart — Sprint 3.9
 *
 * Replaces SalesComparison.jsx. Themed via CSS vars (read once at render
 * from the live document) so dark/light parity works without re-rendering
 * the chart on theme toggle (Apex re-applies on prop change).
 *
 * Backend returns absolute totals; we keep them absolute (formatMoney
 * abbreviates to K/M/B automatically — no more silent "/1_000_000" hack).
 */
function readVar(name, fallback) {
    if (typeof document === 'undefined') return fallback;
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
}

export default function TenantYoYChart({ data, loading, error, onRetry, themeKey }) {
    const { t } = useTranslation();
    const teal = readVar('--v3-teal', '#06B6D4');
    const accent = readVar('--v3-accent', '#3B82F6');
    const grid = readVar('--v3-chart-grid', 'rgba(255,255,255,0.04)');
    const muted = readVar('--v3-text-muted', '#94A3B8');

    const { categories, series, isEmpty } = useMemo(() => {
        const cur = Array.isArray(data?.currentYearSales) ? data.currentYearSales : [];
        const prev = Array.isArray(data?.previousYearSales) ? data.previousYearSales : [];
        if (!cur.length && !prev.length) {
            return { categories: [], series: [], isEmpty: true };
        }
        const cats = cur.map(r => r.month);
        const curYear = cur[0]?.year;
        const prevYear = prev[0]?.year;
        return {
            categories: cats,
            series: [
                { name: curYear ? `${curYear}` : t('Current Year'), data: cur.map(r => Number(r.totalSales) || 0) },
                { name: prevYear ? `${prevYear}` : t('Previous Year'), data: prev.map(r => Number(r.totalSales) || 0) },
            ],
            isEmpty: false,
        };
    }, [data, t]);

    const options = useMemo(() => ({
        chart: {
            type: 'bar',
            toolbar: { show: false },
            foreColor: muted,
            background: 'transparent',
            animations: { enabled: true, speed: 400 },
        },
        colors: [accent, teal],
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '55%',
                borderRadius: 4,
            },
        },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 2, colors: ['transparent'] },
        xaxis: {
            categories,
            axisBorder: { color: grid },
            axisTicks: { color: grid },
            labels: { style: { colors: muted, fontSize: '11px' } },
        },
        yaxis: {
            labels: {
                style: { colors: muted, fontSize: '11px' },
                formatter: (val) => formatMoney(val, ''),
            },
        },
        grid: { borderColor: grid, strokeDashArray: 4 },
        legend: {
            position: 'top',
            horizontalAlign: 'right',
            labels: { colors: muted },
            markers: { width: 10, height: 10, radius: 5 },
        },
        tooltip: {
            theme: 'dark',
            y: { formatter: (v) => formatMoney(v) },
        },
        responsive: [{
            breakpoint: 768,
            options: {
                plotOptions: { bar: { columnWidth: '70%' } },
                legend: { position: 'bottom', horizontalAlign: 'left' },
            },
        }],
    // Re-init when theme flips so colors pick up the new palette.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [categories, accent, teal, grid, muted, themeKey]);

    return (
        <div className="v3-td-card v3-td-section">
            <h3 className="v3-td-card-title">{t('Year-over-Year Sales')}</h3>
            {error ? (
                <div className="v3-td-error">
                    {t('Unable to load YoY data')} — {error}
                    <button type="button" onClick={onRetry}>{t('Retry')}</button>
                </div>
            ) : loading ? (
                <div className="v3-td-skeleton" style={{ height: 320 }} />
            ) : isEmpty ? (
                <div className="v3-td-empty" style={{ margin: '12px 0 0', padding: 24 }}>
                    {t('No sales data available for this tenant yet.')}
                </div>
            ) : (
                <div className="v3-td-chart-wrap">
                    <Chart
                        key={`yoy-${themeKey || ''}`}
                        options={options}
                        series={series}
                        type="bar"
                        height={340}
                    />
                </div>
            )}
        </div>
    );
}
